import {
  useCallback, useMemo, useState, useRef
} from 'react'
import {Box, Notification} from '@binance/uikit'
import useOrderForm from 'hooks/useOrderForm'
import useRouter from 'hooks/useRouter'
import useUserLoginStatus from 'hooks/useUserLoginStatus'
import {useDispatch} from 'redux-react-hook'
import useSymbolInfo from 'hooks/useSymbolInfo'
import {
  updateOrderPrice,
  updateOrderQty,
  resetOrderForm,
  updateOrderTradeStatus,
  updateLimitType,
  updateTimeInForce,
  updateReduceOnly,
} from 'actions'
import {
  TRADE_TYPES, TRADE_SIDES, LIMIT_TYPES, TIME_IN_FORCE
} from 'constants'
import {floor} from 'utils/number'
import RequireLoginEl from './RequireLoginEl'
import LimitForm from './LimitForm'
import ConfirmDialog from './ConfirmDialog'
import TradeLimitType from './TradeLimitType'

export default function LimitFormContainer ({i18n}) {
  const lastTifType = useRef(TIME_IN_FORCE.TIF.GTC)
  const [side, setSide] = useState(TRADE_SIDES.BUY)
  const [showDialog, setShowDialog] = useState(false)
  const dispatch = useDispatch()
  const formType = TRADE_TYPES.LIMIT

  const isLogin = useUserLoginStatus()
  const router = useRouter()
  const {symbol} = router.query
  const {symbolInfo} = useSymbolInfo()

  const {
    baseAsset, quoteAsset, pricePrecision, quantityPrecision, maxLen
  } = symbolInfo

  const {
    handleBuy,
    handleSell,
    availableBalance,
    orderBuyValue,
    marginBuyRequired,
    orderSellValue,
    marginSellRequired,
    maxPrice,
    limitMaxQty,
    buyPrice,
    buyQty,
    sellPrice,
    sellQty,
    buyStatus,
    sellStatus,
    limitType,
    maxBuyQty,
    maxSellQty,
    reduceOnly,
    positionSize
  } = useOrderForm(symbol, quoteAsset, formType)

  const reset = useCallback(
    buyOrSell => {
      dispatch(resetOrderForm(buyOrSell, formType))
    },
    [dispatch, formType]
  )

  const setTypeInForce = useCallback(
    (type, isNotTif) => {
      if (!isNotTif) lastTifType.current = type
      dispatch(updateTimeInForce(formType, type))
    },
    [dispatch, formType]
  )

  const setLimitType = useCallback(
    type => {
      if (type === LIMIT_TYPES.REDUCE_ONLY) {
        dispatch(updateReduceOnly(formType, !reduceOnly))
      } else {
        const isTifType = type === LIMIT_TYPES.TIF
        dispatch(updateLimitType(formType, type))
        const resetLimitType = isTifType ? lastTifType.current : TIME_IN_FORCE.POST_ONLY
        setTypeInForce(resetLimitType, !isTifType)
      }
    },
    [dispatch, formType, reduceOnly, setTypeInForce]
  )

  const switchTradeStatus = useCallback(
    (buyOrSell, status) => {
      dispatch(updateOrderTradeStatus(buyOrSell, status, formType))
    },
    [dispatch, formType]
  )

  const switchDialog = useCallback(() => {
    setShowDialog(!showDialog)
  }, [showDialog])

  const buyHandler = useCallback(
    isConfirmed => {
      const bPrice = Number(buyPrice)
      const bQty = Number(buyQty)
      if (bPrice > 0 && bQty > 0) {
        const tradeSide = TRADE_SIDES.BUY
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleBuy().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['limitForm-buySuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['limitForm-buyFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [buyPrice, buyQty, formType, handleBuy, i18n, reset, switchDialog, switchTradeStatus]
  )

  const sellHandler = useCallback(
    isConfirmed => {
      const sPrice = Number(sellPrice)
      const sQty = Number(sellQty)
      if (sPrice > 0 && sQty > 0) {
        const tradeSide = TRADE_SIDES.SELL
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleSell().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['limitForm-sellSuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['limitForm-sellFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [formType, handleSell, i18n, reset, sellPrice, sellQty, switchDialog, switchTradeStatus]
  )

  const getBuySliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize > 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
      }
      if (maxBuyQty === 0) {
        availableRange = {min: 0, max: 0}
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxBuyQty])

  const getSellSliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize < 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
        if (maxSellQty === 0) {
          availableRange = {min: 0, max: 0}
        }
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxSellQty])

  const changeSliderValue = useCallback(({val, type}) => {
    const maxQty = type === 'buy' ? maxBuyQty : maxSellQty
    val = floor(maxQty * val / 100, quantityPrecision)
    dispatch(updateOrderQty(type, val, formType))
  }, [dispatch, maxBuyQty, maxSellQty, formType, quantityPrecision])

  return useMemo(
    () => (
      <>
        {showDialog && (
          <ConfirmDialog
            i18n={i18n}
            visible={showDialog}
            type={formType}
            side={side}
            onClose={switchDialog}
            handleBuy={buyHandler}
            handleSell={sellHandler}
          />
        )}
        <Box mx={5} my={3}>
          <TradeLimitType
            name="limitRadio"
            i18n={i18n}
            limitType={limitType}
            setLimitType={setLimitType}
            setTypeInForce={setTypeInForce}
            availableBalance={availableBalance}
            reduceOnly={reduceOnly}
            enableReduceOnly={true}
          />
        </Box>
        <LimitForm
          i18n={i18n}
          isLogin={isLogin}
          requireLoginEl={<RequireLoginEl i18n={i18n} />}
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
          pricePrecision={pricePrecision}
          quantityPrecision={quantityPrecision}
          maxLen={maxLen}
          maxPrice={maxPrice}
          limitMaxQty={limitMaxQty}
          buyPrice={buyPrice}
          buyQty={buyQty}
          sellPrice={sellPrice}
          sellQty={sellQty}
          orderBuyValue={orderBuyValue}
          marginBuyRequired={marginBuyRequired}
          orderSellValue={orderSellValue}
          marginSellRequired={marginSellRequired}
          handlePriceChange={(buyOrSell, price) => {
            dispatch(updateOrderPrice(buyOrSell, price, formType))
          }}
          handleQtyChange={(buyOrSell, qty) => {
            dispatch(updateOrderQty(buyOrSell, qty, formType))
          }}
          handleBuy={buyHandler}
          handleSell={sellHandler}
          buyStatus={buyStatus}
          sellStatus={sellStatus}
          reduceOnly={reduceOnly}
          buyAvailableRange={getBuySliderAvailableRange()}
          sellAvailableRange={getSellSliderAvailableRange()}
          changeSliderValue={changeSliderValue}
        />
      </>
    ),
    [showDialog, i18n, formType, side, switchDialog, buyHandler, sellHandler, limitType, setLimitType, setTypeInForce, availableBalance, reduceOnly, isLogin, baseAsset, quoteAsset, pricePrecision, quantityPrecision, maxLen, maxPrice, limitMaxQty, buyPrice, buyQty, sellPrice, sellQty, orderBuyValue, marginBuyRequired, orderSellValue, marginSellRequired, buyStatus, sellStatus, getBuySliderAvailableRange, getSellSliderAvailableRange, changeSliderValue, dispatch]
  )
}
